import { ChangeDetectionStrategy, Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { MatStepper } from '@angular/material/stepper';
import { BehaviorSubject, combineLatest, Observable, Subject } from 'rxjs';
import { debounceTime, filter, map, startWith, take, takeUntil, withLatestFrom } from 'rxjs/operators';
import { Frac, FracStageMesh, FracStorage, StageMeshVolumeUpdate, StorageCurrentStock } from '~lmo/models/frac.model';
import { GroupedMeshes, Mesh, sortByPreferredOrder } from '~lmo/models/mesh.model';
import { LmoConstantsService, LmoFracsService } from '~lmo/services';
import { LmoFracModalService } from '~lmo/services/lmo-frac-modal.service';
import { NptReport } from '~lmo/services/lmo-fracs.service';
import { trackById } from '~lmo/utilities';
import { joinWithAnd } from '~utilities/joinWithAnd';
import { ConfirmUpdateStageDialogComponent } from '../../../ui-components/confirm-update-stage-dialog/confirm-update-stage-dialog.component';

@Component({
  changeDetection: ChangeDetectionStrategy.OnPush,
  selector: 'sa-lmo-complete-stage',
  styleUrls: ['./lmo-complete-stage.component.scss'],
  templateUrl: './lmo-complete-stage.component.html',
})
export class LmoCompleteStageComponent implements OnInit, OnDestroy {
  public nptWarningStringOne: string;
  public nptWarningStringTwo: string;
  public nptWarning$ = new BehaviorSubject<boolean>(false);
  @ViewChild(MatStepper, { static: false }) private stepper: MatStepper;
  public checkForNpt$ = new BehaviorSubject(false);
  private destroy$: Subject<null> = new Subject<null>();
  public frac$: Observable<Frac>;
  public meshesInOrder$: Observable<Mesh[]>;
  public meshesInOrderGroup$: Observable<GroupedMeshes[]>;
  public storagesInOrder$: Observable<FracStorage[]>;
  public pumpedForm: FormGroup;
  public currentStockForm: FormGroup;
  public trackById = trackById;
  public isReady$$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  public emptyForm: FormGroup = new FormGroup({});

  private expectedCurrentMap = new Map<number, number>();
  private fracStorageVsMeshName = new Map<number, string>();

  private expectedPumpedMap = new Map<number, number>();
  private meshIdVsMeshName = new Map<number, string>();

  constructor(
    private fracService: LmoFracsService,
    private fb: FormBuilder,
    private modalService: LmoFracModalService,
    private constants: LmoConstantsService,
    private dialog: MatDialog,
  ) {
    this.setupMesh = this.setupMesh.bind(this);
  }

  public ngOnInit() {
    this.frac$ = this.fracService.currentFrac$.pipe(filter((frac) => !!frac));

    this.setupMeshAndStorageOrder();

    combineLatest([
      this.fracService.currentFracCurrentStage$.pipe(filter((currentStage) => !!currentStage)),
      this.frac$,
    ])
      .pipe(take(1))
      .subscribe(([currentStage, frac]) => {
        this.pumpedForm = this.fb.group({});
        this.currentStockForm = this.fb.group({});
        currentStage.meshes.forEach((mesh) => this.setupMesh(mesh, frac));

        frac.fracStorages.forEach((fracStorage, storageIndex) => {
          (fracStorage.supplementalMeshes || []).forEach((mesh, meshIndex) => {
            const fracStageMesh: FracStageMesh = {
              id: mesh.meshId,
              isSupplemental: true,
              preferredOrder: 0,
              type: mesh.meshName,
              volume: 0,
            };
            this.setupMesh(fracStageMesh, frac);
          });
        });
        this.isReady$$.next(true);
      });
  }

  public ngOnDestroy(): void {
    this.destroy$.next(null);
    this.destroy$.unsubscribe();
  }

  public async sendStageComplete(hasNPT = false): Promise<void> {
    const nptReports = await this.getNptReports();
    const frac = await this.frac$.pipe(take(1)).toPromise();
    if (!frac.autoStorageUpdate && nptReports.length && !hasNPT) {
      await this.warnAboutNPT(nptReports);
      return;
    }
    const meshes: Record<string, number> = this.pumpedForm.value;
    const stageMeshVolumeUpdate: StageMeshVolumeUpdate[] = Object.keys(meshes)
      .map((key) => [+key, meshes[key]] as [number, number])
      .map(([meshId, actualVolume]) => ({
        actualVolume,
        meshId,
      }));
    const storages: Record<string, number> = this.currentStockForm.value;
    const storageCurrentStocks: StorageCurrentStock[] = Object.keys(storages)
      .map((key) => [+key, storages[key]] as [number, number])
      .map(([storageId, currentStock]) => ({
        currentStock,
        storageId,
      }));

    const issues = [];

    const pumpedStockIssue = {
      fields: [],
      name: `Pumped Stock`,
    };
    stageMeshVolumeUpdate.forEach((update) => {
      const meshType = this.meshIdVsMeshName[update.meshId];
      const expectedStock = this.expectedPumpedMap[update.meshId];
      const updateStock = update.actualVolume;

      let message = '';
      if (updateStock > expectedStock * 2) {
        message =
          'Your reported pumped stock <span class="error-text">' +
          updateStock.toLocaleString() +
          'lbs</span> for <b>' +
          meshType +
          ' mesh</b> is very high compared to system calculated pumped stock of <b>' +
          expectedStock.toLocaleString() +
          'lbs</b>';
      } else if (updateStock < expectedStock * 0.2) {
        message =
          'Your reported pumped stock <span class="error-text">' +
          updateStock.toLocaleString() +
          'lbs</span> for <b>' +
          meshType +
          '</b> mesh is very low compared to system calculated pumped stock of <b>' +
          expectedStock.toLocaleString() +
          'lbs</b>';
      }
      if (message !== '') {
        pumpedStockIssue.fields.push(message);
      }
    });
    if (pumpedStockIssue.fields.length > 0) {
      issues.push(pumpedStockIssue);
    }

    const currentStockIssue = {
      fields: [],
      name: `Current Stock`,
    };
    storageCurrentStocks.forEach((stock) => {
      const meshType = this.fracStorageVsMeshName[stock.storageId];
      const expectedStock = this.expectedCurrentMap[stock.storageId];
      const updateStock = stock.currentStock;

      let message = '';
      if (updateStock > expectedStock * 2) {
        message =
          'Your reported current stock <span class="error-text">' +
          updateStock.toLocaleString() +
          'lbs</span> for <b>' +
          meshType +
          ' mesh</b> is very high compared to system calculated current stock of <b>' +
          expectedStock.toLocaleString() +
          'lbs</b>';
      } else if (updateStock < expectedStock * 0.2) {
        message =
          'Your reported current stock <span class="error-text">' +
          updateStock.toLocaleString() +
          'lbs</span> for <b>' +
          meshType +
          ' mesh</b> is very low compared to system calculated current stock of <b>' +
          expectedStock.toLocaleString() +
          'lbs</b>';
      }
      if (message !== '') {
        currentStockIssue.fields.push(message);
      }
    });
    if (currentStockIssue.fields.length > 0) {
      issues.push(currentStockIssue);
    }

    if (issues.length > 0) {
      const scheduleConfirmDialog = this.dialog.open(ConfirmUpdateStageDialogComponent, {
        data: issues,
        maxWidth: '968px',
        width: '50%',
      });
      scheduleConfirmDialog.afterClosed().subscribe((confirm) => {
        if (confirm) {
          this.fracService
            .completeStage$(stageMeshVolumeUpdate, storageCurrentStocks, nptReports)
            .subscribe((result) => {
              if (result) {
                this.modalService.closeHomeModal();
                this.fracService.fetchCurrentFrac();
              }
            });
        }
      });
    } else {
      this.fracService.completeStage$(stageMeshVolumeUpdate, storageCurrentStocks, nptReports).subscribe((result) => {
        if (result) {
          this.modalService.closeHomeModal();
          this.fracService.fetchCurrentFrac();
        }
      });
    }
  }

  private async warnAboutNPT(reports: NptReport[]) {
    const meshes = await this.constants.meshesById$.pipe(take(1)).toPromise();
    const meshInfo: { mesh: Mesh; scheduledPumpVolume: number }[] = reports.map((r) => ({
      mesh: meshes[r.meshId],
      scheduledPumpVolume: r.scheduledPumpVolume,
    }));
    meshInfo.sort((a, b) => a.mesh.preferredOrder - b.mesh.preferredOrder);
    const meshNames = meshInfo.map((m) => `<b>${m.mesh.type} mesh</b>`);
    this.nptWarningStringOne = `Your reported stock of ${joinWithAnd(meshNames)} ${
      meshNames.length === 1 ? 'is' : 'are'
    } lower than what is needed to start the next stage. `;
    const meshNeeds = joinWithAnd(
      meshInfo.map((m) => `<b>${m.scheduledPumpVolume.toLocaleString()}lbs ${m.mesh.type} mesh</b>`),
    );
    this.nptWarningStringTwo = `Please confirm you are on NPT, or set the stock to at least ${meshNeeds} to begin the next stage.`;
    this.checkForNpt$.next(true);
    await new Promise((resolve) => setTimeout(resolve, 50));
    this.stepper.next();
  }

  public backToCurrentStock() {
    this.nptWarning$.next(true);
    this.checkForNpt$.next(false);
    this.stepper.previous();
  }

  private async getNptReports(): Promise<NptReport[]> {
    const frac = await this.frac$.pipe(take(1)).toPromise();
    const meshAmountRemaining: Record<string, number> = getMeshAmountRemaining(this.currentStockForm.value, frac);
    const nextStageVolumes = getNextStageVolumes(frac);
    if (!nextStageVolumes) {
      return [];
    }
    const nptReports: NptReport[] = Object.keys(meshAmountRemaining)
      .map((meshId) => {
        const currentStock = meshAmountRemaining[meshId];
        const scheduledPumpVolume = nextStageVolumes[meshId];
        if (!scheduledPumpVolume || currentStock >= scheduledPumpVolume) {
          return null;
        }
        return {
          currentStock,
          meshId: +meshId,
          scheduledPumpVolume,
        };
      })
      .filter((x) => !!x);
    return nptReports;
  }

  private setupMeshAndStorageOrder() {
    this.meshesInOrder$ = this.frac$.pipe(
      map((frac) => frac.fracStorages.map((storage) => storage.mesh)),
      map((meshes) => sortByPreferredOrder(meshes)),
    );
    this.meshesInOrderGroup$ = this.frac$.pipe(
      map((frac) => {
        const uniqueMeshRecord = (frac.fracStorages || []).reduce((meshRecord, storage) => {
          meshRecord[storage.id] = {
            meshName: storage.mesh.type,
            meshes: [storage.mesh],
            storageId: storage.id,
          } as GroupedMeshes;
          return meshRecord;
        }, {});

        frac.fracStorages.forEach((fracStorage, storageIndex) => {
          (fracStorage.supplementalMeshes || []).forEach((mesh, meshIndex) => {
            const currentLength = uniqueMeshRecord[fracStorage.id].meshes.length;
            const supplementalMesh: Mesh = {
              id: mesh.meshId,
              isSupplemental: true,
              preferredOrder: fracStorage.mesh.preferredOrder + currentLength,
              type: mesh.meshName,
            };
            uniqueMeshRecord[fracStorage.id].meshes.push(supplementalMesh);
          });
        });

        return Object.keys(uniqueMeshRecord).map((key) => uniqueMeshRecord[key]);
      }),
    );
    this.storagesInOrder$ = combineLatest([this.meshesInOrder$, this.frac$]).pipe(
      map(([meshes, frac]) => {
        // This is O(n^2) but there are usually only 2 - 5 meshes per frac so I don't care.
        const storages: FracStorage[] = meshes.map((mesh) =>
          frac.fracStorages.find((storage) => storage.mesh.id === mesh.id),
        );
        return storages;
      }),
    );
  }

  private setupMesh(mesh: FracStageMesh, frac: Frac) {
    const meshId = `${mesh.id}`;

    let foundStorage: FracStorage;

    storageIdCheck: for (let i = 0; i < frac.fracStorages.length; i++) {
      const storage = frac.fracStorages[i];
      if (storage.mesh.id === mesh.id) {
        foundStorage = storage;
        break;
      }

      if (foundStorage === undefined && !!storage.supplementalMeshes) {
        for (let j = 0; j < storage.supplementalMeshes.length; j++) {
          const supplementalMesh = storage.supplementalMeshes[j];
          if (supplementalMesh.meshId === mesh.id) {
            foundStorage = storage;
            break storageIdCheck;
          }
        }
      }
    }

    const matchingStorageId = `${foundStorage.id}`;

    this.meshIdVsMeshName[mesh.id] = mesh.type;
    this.expectedPumpedMap[mesh.id] = mesh.volume;

    this.pumpedForm.addControl(meshId, this.fb.control('', [Validators.required]));
    const control = this.fb.control(null, [Validators.required]);
    if (frac.autoStorageUpdate) {
      control.disable();
    }
    this.currentStockForm.addControl(matchingStorageId, control);
    this.pumpedForm
      .get(meshId)
      .valueChanges.pipe(
        startWith(mesh.volume),
        debounceTime(300),
        withLatestFrom(this.frac$),
        takeUntil(this.destroy$),
      )
      .subscribe(([pumpedStock, f]: [number, Frac]) => {
        if (pumpedStock !== null) {
          const currentStock =
            foundStorage && foundStorage.currentStock ? Math.max(foundStorage.currentStock - pumpedStock, 0) : 0;
          this.expectedCurrentMap[foundStorage.id] = currentStock;
          this.fracStorageVsMeshName[foundStorage.id] = mesh.type;
          // this.currentStockForm.get(matchingStorageId).setValue(currentStock);
        }
      });
  }
}

function getNextStageVolumes(frac: Frac): Record<number, number> {
  const nextStageNumber = frac.currentStage + 1;
  const nextStage = frac.fracStages.find((s) => s.stageNumber === nextStageNumber);
  if (!nextStage) {
    return null;
  }
  return nextStage.meshes.reduce(
    (acc, mesh) => ({
      ...acc,
      [mesh.id]: mesh.volume,
    }),
    {},
  );
}

function getMeshAmountRemaining(storageAmountRemaining: Record<string, number>, frac: Frac): Record<number, number> {
  const storageToMeshID: Record<string, number> = frac.fracStorages.reduce(
    (acc, storage) => ({
      ...acc,
      [storage.id]: storage.mesh.id,
    }),
    {},
  );
  return Object.keys(storageAmountRemaining).reduce(
    (acc, storageID) => ({
      ...acc,
      [storageToMeshID[storageID]]: storageAmountRemaining[storageID],
    }),
    {},
  );
}
